import {
  AppStatus,
  Chat,
  Config,
  Country,
  CreateThreadPayload,
  Entity,
  NPSStatusResponse,
  Preferences,
  StrategyResult,
  Thread,
  ThreadSearchResult,
  ThreadWithChatIds,
  Topic,
  UpdateThreadPayload,
  User,
  UserPreferences,
} from '@/types';

import { createChatFilter } from '../../../utils/chat-filter';
import { Region, SubscriptionSettings } from '../../admin/managers/subscriptions/data';
import { FeedbackPayload } from '../../thread/types';
import { TSpanEvent } from '../../trace/types';
import { Fetch } from './api';
import { BlueJApi } from './blue-j-api';
import { NotFoundError } from './error';
import { OrganizationAdmin, OrganizationUsersPaginatedResponse, SetOrganizationAdminPayload } from '../../admin/managers/organizations/components/list-paginated-users/types';
import { OrganizationAdminEventsPaginatedResponse } from '../../administration/audit-log/data';
import { FeatureUpdate, OrganizationFeatures } from '../../administration/features/data';
import { OrganizationContact, OrganizationContactPayload, OrganizationContactType, UserOrganizationAdminOrganizationEntity, UserOrganizationAdminOrganizationUpdatePayload } from '../../administration/organization/data';
import { UserListSortDirection, UserListSortOptions } from '../../administration/users/user-list';

export interface IAskBlueJApi {
  getChat: (threadId: string, chatId: string, includePending: boolean) => Promise<Chat>;
  abortChat: (threadId: string, chatId: string) => Promise<Chat>;
  createThread: (topic: string, initialThreadName: string) => Promise<Thread>;
  getThread: (threadId: string, includePending?: boolean) => Promise<Thread>;
  getThreads: (options?: { pinned?: boolean }) => Promise<Thread[]>;
  updateThread: (threadId: string, title: string) => Promise<Thread>;
  searchThreads: (query: string) => Promise<ThreadSearchResult>;
  deleteThread: (threadId: string) => Promise<void>;
  sendFeedback: (data: FeedbackPayload) => Promise<void>;
  recommendFollowUp: (threadId: string, question: string, answer: string, variant: Country) => Promise<string[]>;
  whoami: () => Promise<User>;
  deleteChat: (threadId: string, chatId: string) => Promise<void>;
  getStrategies: () => Promise<StrategyResult>;
  getUserNPSStatus: () => Promise<NPSStatusResponse>;
  getUserPreferences: () => Promise<UserPreferences>;
  updateUserPreferences: (preferences: Partial<Preferences>) => Promise<UserPreferences>;
  getTrace: (chatId: string) => Promise<TSpanEvent[]>;
  expectation: (question: string, strategy: string) => Promise<{ criteria: string; }>;
  getSubscriptionSettings: () => Promise<SubscriptionSettings>;
  getBillingPortalUrl: (region: Region) => Promise<{ url: string }>;
  getSetPasswordUrl: () => Promise<{ url: string }>;
  getUserOrganization: () => Promise<{ id: string, name: string, sector: string, webSearchEnabled: boolean, projectFilesEnabled: boolean }[]>;
  getOrganization: () => Promise<UserOrganizationAdminOrganizationEntity>;
  updateOrganization: (organization: UserOrganizationAdminOrganizationUpdatePayload) => Promise<UserOrganizationAdminOrganizationEntity>;
  getOrganizationContact: (type: OrganizationContactType) => Promise<OrganizationContact>;
  updateOrganizationContact: (contact: OrganizationContactPayload) => Promise<OrganizationContact>;
  getOrganizationFeatures: () => Promise<OrganizationFeatures>;
  updateOrganizationWebSearchFeature: (payload: FeatureUpdate) => Promise<FeatureUpdate>;
  updateOrganizationFileUploadFeature: (payload: FeatureUpdate) => Promise<FeatureUpdate>;
  getOrganizationEvents: (page: number, perPage: number, eventType?: string) => Promise<OrganizationAdminEventsPaginatedResponse>;
  getOrganizationUsers: (page: number, perPage: number, sortBy: UserListSortOptions, sortDirection: UserListSortDirection, queryFilter: string, roleFilter: string) => Promise<OrganizationUsersPaginatedResponse>;
  setOrganizationUserAdmin: (userId: string, data: SetOrganizationAdminPayload) => Promise<OrganizationAdmin>;
  getTopics: () => Promise<{ topics: Topic[] }>;
  getAppStatus: () => Promise<AppStatus>;
}

export class AskBlueJApi extends BlueJApi implements IAskBlueJApi {
  private basePath = 'api';

  private _getChat = this.createFetch<null, Chat, { threadId: string, chatId: string, includePending: boolean }>(params => `threads/${params.threadId}/chats/${params.chatId}?includePending=${params.includePending}`, 'GET');
  private _abortChat = this.createFetch<{ threadId: string, chatId: string }, Chat, void>(`${this.basePath}/abort-chat`, 'POST');
  private _getThread = this.createFetch<null, ThreadWithChatIds, { threadId: string }>(params => {
    const { threadId } = params;
    return `${this.basePath}/thread-meta/${threadId}`;
  }, 'GET');
  private _createThread = this.createFetch<CreateThreadPayload, ThreadWithChatIds, void>(`${this.basePath}/create-thread`, 'POST');
  private _getThreads = this.createFetch<{ pinned?: boolean } | null, Thread[], void>(`${this.basePath}/all-threads`, 'GET');
  private _deleteThread = this.createFetch<{ threadId: string }, Thread, void>(`${this.basePath}/delete-thread`, 'POST');
  private _sendFeedback = this.createFetch<FeedbackPayload, void, void>(`${this.basePath}/feedback`, 'POST');
  private _updateThread = this.createFetch<UpdateThreadPayload, Thread, void>(`${this.basePath}/update-thread`, 'POST');
  private _recommendFollowUp = this.createFetch<{ threadId: string, question: string, answer: string, variant: Country }, string[], void>(`${this.basePath}/recommend-followup`, 'POST');
  private _whoami = this.createFetch<null, User, void>(`${this.basePath}/whoami`, 'POST');
  private _deleteChat = this.createFetch<{ threadId: string, chatId: string }, Chat, void>(`${this.basePath}/delete-chat`, 'POST');
  private _getStrategies = this.createFetch<null, StrategyResult, null>(`${this.basePath}/manage/strategies`, 'GET');
  private _getUserNPSStatus = this.createFetch<null, NPSStatusResponse, null>(`${this.basePath}/feedback/nps-eligibility`, 'GET');
  private _sendNPSResponse = this.createFetch<{ score: number, feedback: string }, void, void>(`${this.basePath}/feedback/nps`, 'POST');
  private _getUserPreferences = this.createFetch<null, UserPreferences, null>(`${this.basePath}/get-user-preferences`, 'GET');
  private _updateUserPreferences = this.createFetch<Partial<Preferences>, UserPreferences, void>(`${this.basePath}/update-user-preferences`, 'POST');
  private _getTrace = this.createFetch<null, TSpanEvent[], { chatId: string }>(params => `${this.basePath}/chat/${params.chatId}/trace`, 'GET');
  private _expectation = this.createFetch<{ question: string, strategy: string }, { criteria: string }, void>(`${this.basePath}/expectation`, 'POST');
  private _getSubscriptionSettings = this.createFetch<null, SubscriptionSettings, void>(`${this.basePath}/subscription/settings`, 'GET');
  private _getBillingPortalUrl = this.createFetch<{ region: Region }, { url: string }, void>(`${this.basePath}/subscription/get-billing-portal`, 'GET');
  private _getSetPasswordUrl = this.createFetch<null, { url: string }, void>(`${this.basePath}/users/current/get-set-password-url`, 'POST');
  private _searchThreads = this.createFetch<{ query: string }, ThreadSearchResult, void>(`${this.basePath}/search`, 'POST');
  private _generateTaxNotesContentViewerToken = this.createFetch<{ resource_id: string, entity?: Entity, document_id?: string }, { taxNotesToken: string }, void>(`${this.basePath}/tax-notes-content-viewer/generate-token`, 'POST');
  private _getUserOrganization = this.createFetch<null, { id: string, name: string, sector: string, webSearchEnabled: boolean, projectFilesEnabled: boolean, taxNotesContentViewerEnabled: boolean }[], void>(`${this.basePath}/users/current/organization`, 'GET');
  private _getOrganization = this.createFetch<null, UserOrganizationAdminOrganizationEntity, void>(`${this.basePath}/organization-admin/organization`, 'GET');
  private _updateOrganization = this.createFetch<UserOrganizationAdminOrganizationUpdatePayload, UserOrganizationAdminOrganizationEntity, void>(`${this.basePath}/organization-admin/organization`, 'POST');
  private _getOrganizationContact = this.createFetch<null, OrganizationContact, { type: OrganizationContactType }>(
    (params) => `${this.basePath}/organization-admin/contact?type=${params.type}`,
    'GET'
  );
  private _updateOrganizationContact = this.createFetch<OrganizationContactPayload, OrganizationContact, void>(`${this.basePath}/organization-admin/contact`, 'POST');
  private _getOrganizationFeatures = this.createFetch<null, OrganizationFeatures, void>(`${this.basePath}/organization-admin/features`, 'GET');
  private _updateOrganizationWebSearchFeature = this.createFetch<FeatureUpdate, FeatureUpdate, void>(`${this.basePath}/organization-admin/features/web-search`, 'POST');
  private _updateOrganizationFileUploadFeature = this.createFetch<FeatureUpdate, FeatureUpdate, void>(`${this.basePath}/organization-admin/features/file-upload`, 'POST');
  private _getOrganizationUsers = this.createFetch<null, OrganizationUsersPaginatedResponse, { page: number, perPage: number, sortBy: UserListSortOptions, sortDirection: UserListSortDirection, queryFilter: string, roleFilter: string }>(({ page, perPage, sortBy, sortDirection, queryFilter, roleFilter }) => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      perPage: perPage.toString(),
      sortBy: sortBy,
      sortDirection: sortDirection,
      query: queryFilter ?? '',
    });
    if (roleFilter) {
      queryParams.append('role', roleFilter);
    }
    return `${this.basePath}/organization-admin/users?${queryParams.toString()}`;
  }, 'GET');
  private _setOrganizationUserAdmin = this.createFetch<SetOrganizationAdminPayload, OrganizationAdmin, { userId: string }>(
    (params) => `${this.basePath}/organization-admin/users/${params.userId}/admin`,
    'POST'
  );
  private _getOrganizationEvents = this.createFetch<null, OrganizationAdminEventsPaginatedResponse, { page: number, perPage: number, eventType: string }>(({ page, perPage, eventType }) => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      perPage: perPage.toString(),
    });
    if (eventType) {
      queryParams.append('eventType', eventType);
    }
    return `${this.basePath}/organization-admin/events?${queryParams.toString()}`;
  }, 'GET');
  
  private _getTopics = this.createFetch<null, { topics: Topic[] }, void>(`${this.basePath}/manage/topics`, 'GET');
  private _getAppStatusFromWorker = this.createFetch<null, AppStatus, void>('status', 'GET');
  private _config = this.createFetch<null, Config, void>('config', 'GET');

  constructor(backendUrl = '', overriddenFetch?: Fetch | undefined) {
    super(backendUrl, overriddenFetch);
  }

  public async getChat(threadId: string, chatId: string, includePending = false) {
    return this._getChat(null, { threadId, chatId, includePending }).parsedResponse();
  }

  public async abortChat(threadId: string, chatId: string) {
    return this._abortChat({ threadId, chatId }).parsedResponse();
  }

  public async createThread(topic: string, initialThreadName: string) {
    const thread = await this._createThread({ topic, initialThreadName }).parsedResponse();
    return {
      ...thread,
      chatIds: thread.chats.map(chat => chat.chat_id),
      chats: []
    }
  }

  public async getThread(threadId: string, includePending = false) {
    const thread = await this._getThread(null, { threadId }).parsedResponse();
    const filteredThread: Thread = {
      ...thread,
      chatIds: thread.chats
        .filter(createChatFilter(includePending))
        .map(({ chat_id }) => chat_id),
      chats: [],
    }
    return filteredThread
  }

  public async getThreads(options?: { pinned?: boolean, limit?: number }) {
    return this._getThreads(options ?? null).parsedResponse();
  }

  public async updateThread(threadId: string, title: string) {
    return this._updateThread({ threadId, title, updateDate: false }).parsedResponse();
  }

  public async searchThreads(query: string) {
    return this._searchThreads({ query }).parsedResponse();
  }

  public async generateTaxNotesContentViewerToken(resource_id: string, entity?: Entity, document_id?: string) {
    return this._generateTaxNotesContentViewerToken({ resource_id, entity, document_id }).parsedResponse();
  }

  public async deleteThread(threadId: string) {
    await this._deleteThread({ threadId }).parsedResponse().catch((err) => {
      if (err instanceof NotFoundError) {
        return {};
      }
      throw err;
    });
  }

  public async sendFeedback(data: FeedbackPayload) {
    return this._sendFeedback(data).parsedResponse();
  }

  public async whoami() {
    return this._whoami().parsedResponse();
  }

  public async getStrategies() {
    return this._getStrategies().parsedResponse();
  }

  public async deleteChat(threadId: string, chatId: string) {
    await this._deleteChat({ threadId, chatId }).parsedResponse();
  }

  public async getUserNPSStatus() {
    return this._getUserNPSStatus().parsedResponse();
  }

  public async sendNPSResponse(score: number, feedback: string) {
    return this._sendNPSResponse({ score, feedback }).parsedResponse();
  }

  public async getUserPreferences() {
    return this._getUserPreferences().parsedResponse();
  }

  public async updateUserPreferences(preferences: Partial<Preferences>) {
    return this._updateUserPreferences(preferences).parsedResponse();
  }

  public async getTrace(chatId: string) {
    return this._getTrace(null, { chatId }).parsedResponse();
  }

  public async recommendFollowUp(threadId: string, question: string, answer: string, variant: Country) {
    return this._recommendFollowUp({ threadId, question, answer, variant }).parsedResponse();
  }

  public async expectation(question: string, strategy: string) {
    return this._expectation({ question, strategy }).parsedResponse();
  }

  public async getSubscriptionSettings() {
    return this._getSubscriptionSettings().parsedResponse();
  }

  public async getBillingPortalUrl(region: Region) {
    return this._getBillingPortalUrl({ region }).parsedResponse();
  }

  public async getSetPasswordUrl() {
    return this._getSetPasswordUrl().parsedResponse();
  }

  public async getUserOrganization() {
    return this._getUserOrganization().parsedResponse();
  }

  public async getOrganization() {
    return this._getOrganization().parsedResponse();
  }

  public async updateOrganization(organization: UserOrganizationAdminOrganizationUpdatePayload) {
    return this._updateOrganization(organization).parsedResponse();
  }

  public async getOrganizationContact(type: OrganizationContactType) {
    return this._getOrganizationContact(null, { type }).parsedResponse();
  }

  public async updateOrganizationContact(contact: OrganizationContactPayload) {
    return this._updateOrganizationContact(contact).parsedResponse();
  }

  public async getOrganizationFeatures() {
    return this._getOrganizationFeatures().parsedResponse();
  }

  public async updateOrganizationWebSearchFeature(payload: FeatureUpdate) {
    return this._updateOrganizationWebSearchFeature(payload).parsedResponse();
  }

  public async updateOrganizationFileUploadFeature(payload: FeatureUpdate) {
    return this._updateOrganizationFileUploadFeature(payload).parsedResponse();
  }
  
  public async getOrganizationUsers(page: number, perPage: number, sortBy: UserListSortOptions, sortDirection: UserListSortDirection, queryFilter: string, roleFilter: string) {
    return this._getOrganizationUsers(null, { page, perPage, sortBy, sortDirection, queryFilter, roleFilter }).parsedResponse();
  }

  public async setOrganizationUserAdmin(userId: string, data: SetOrganizationAdminPayload) {
    return this._setOrganizationUserAdmin(data, { userId }).parsedResponse();
  }

  public async getOrganizationEvents(page: number, perPage: number, eventType = '') {
    return this._getOrganizationEvents(null, { page, perPage, eventType }).parsedResponse();
  }

  public async getTopics() {
    return this._getTopics().parsedResponse();
  }

  public async getAppStatus() {
    return this._getAppStatusFromWorker().parsedResponse();
  }

  public async getConfig() {
    return this._config().parsedResponse();
  }
}
